<script setup lang="ts">
import type { RoamingCountryStruct } from "@/struct/RoaminStruct";
import {
  ref,
  computed,
  watch,
  defineModel,
  useTemplateRef,
  onMounted,
} from "vue";
import RoamingCountersListWindow from "@/components/roaming/RoamingCountersListWindow.vue";
import BaseIcon from "@/components/base/BaseIcon.vue";
import { useI18n } from "vue-i18n";
import { useStore } from "@/store";

const selectedCountry = defineModel<RoamingCountryStruct | null>({
  default: null,
});
const roamingSearchInputElement = useTemplateRef<HTMLDivElement>(
  "roaming-search-input"
);

const { t } = useI18n();
const store = useStore();

const search = ref("");
const isShowSelection = ref(false);

const countersList = computed(() => store.state.roaming.searchCountries);

const getCounters = (search = "" as string) => {
  store.dispatch("roaming/fetchSearchCountries", search);
};

const openSelection = () => {
  isShowSelection.value = true;
};
const closeSelection = () => {
  isShowSelection.value = false;
};
const setFocus = () => {
  if (!roamingSearchInputElement.value) return;
  roamingSearchInputElement.value.focus();
};
const selectCountry = (country: RoamingCountryStruct) => {
  selectedCountry.value = country;
};
const onInput = (event: InputEvent & { target: HTMLInputElement }) => {
  search.value = event.target.value.replace(/[^А-Яа-яЁё]/g, "");
  getCounters(search.value);
};
const isMatchSelectedSearch = computed(
  () => selectedCountry.value?.country_name_ru === search.value
);

watch(
  () => selectedCountry.value,
  (newSelectedCountry) => {
    if (!newSelectedCountry) return;
    search.value = newSelectedCountry.country_name_ru;
    isShowSelection.value = false;
    getCounters(search.value);
  }
);
watch(
  () => isMatchSelectedSearch.value,
  (newIsMatchSelectedSearch) => {
    if (!newIsMatchSelectedSearch) {
      selectedCountry.value = null;
    }
  }
);
onMounted(() => {
  getCounters();
});
</script>

<template>
  <div class="roaming-search">
    <div class="roaming-search__fields">
      <div class="roaming-search__fields-wrapper">
        <input
          class="roaming-search__fields-input caption-medium"
          ref="roaming-search-input"
          type="text"
          v-model="search"
          @focus="openSelection"
          @input="onInput"
          :placeholder="t('roaming.search.placeholder')"
          maxlength="60"
        />
        <Transition name="fade" mode="out-in">
          <div
            v-if="!isShowSelection"
            @click="setFocus"
            class="roaming-search__fields-icon color-grey-blue"
          >
            <BaseIcon name="search" />
          </div>
          <div
            v-else
            @click="closeSelection"
            class="roaming-search__fields-icon --close color-grey-blue"
          >
            <BaseIcon name="close" />
          </div>
        </Transition>
      </div>
    </div>
    <Transition name="drop" mode="out-in">
      <RoamingCountersListWindow
        v-if="countersList && isShowSelection"
        class="roaming-search__selection"
        :countersList="countersList"
        :uuidActive="selectedCountry?.uuid"
        :isSelectType="true"
        @select="selectCountry"
      />
    </Transition>
  </div>
</template>

<style scoped lang="scss">
@import "@/assets/style/colors";

.roaming-search {
  position: relative;
  &__fields {
    width: 100%;
    height: 56px;
    border-radius: 16px;
    background: var(--color-bg-primary);
    border: 1px solid #c7cfd800;
    @media (max-width: 768px) {
      background: #6568731a;
      border-radius: 12px;
    }
    &-wrapper {
      height: 100%;
      padding: 16px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 8px;
      width: 100%;
    }
    &-select {
      display: flex;
      gap: 8px;
      &__image {
        width: 20px;
      }
    }
    &-icon {
      &.--close {
        cursor: pointer;
      }
    }
    &-input {
      font-weight: 500;
      font-size: 16px;
      line-height: 130%;
      color: var(--color-text-primary);
      flex: 1;
      min-width: 50px;
      &::placeholder {
        color: var(--color-text-primary);
      }
    }
  }
  &__selection {
    top: calc(100% + 6px);
    position: absolute;
  }
}
.roaming-search__fields .fade {
  &-enter-active,
  &-leave-active {
    transition: opacity 0.1s ease-in-out;
  }
}
.drop {
  &-enter-from,
  &-leave-to {
    display: grid;
    opacity: 0.5;
    grid-template-rows: 0fr;
  }
  &-enter-to,
  &-leave-from {
    display: grid;
    opacity: 1;
    grid-template-rows: 1fr;
  }
  &-enter-active,
  &-leave-active {
    transition: all 0.2s ease;
    overflow: hidden;
  }
}
</style>
